import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Card, CardContent, IconButton, Chip, Skeleton, Stack, Typography } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import MUIDataTable, { MUIDataTableOptions } from 'mui-datatables';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import AttToken from '../../helpers/attToken';
import { trackEventMatomo } from '../../helpers/matomo';
import Execucoes, { EXECUTION_LEASE_SESSION_KEY, ExecutionLeaseSession, ExecucaoPausada, PausadasCursor } from '../../service/Execucoes';
import queryClient from '../../service/query';
import { FormToFilling } from '../../types/new-form-filling/types';
import { ExecutionContext } from '../NewFormFilling/Structure/NewFormFilling';
import { getMuiTheme } from "../TableHelpers/options";
import { tableOptions } from '../TableHelpers/TableOptions';
import FormFillingModal from './FormFillingModal';
import Modelos from '../../service/Modelos';

const modelosService = new Modelos();
const execucoesService = new Execucoes();
const PAUSED_EXECUTIONS_PAGE_SIZE = 10;

const hasChecklistItems = (checklist: FormToFilling | undefined | null) =>
  Array.isArray(checklist?.itens) && checklist.itens.length > 0;
type ExpirationState = 'pausa' | 'execucao' | null;

const getExpirationState = (execution: Pick<ExecucaoPausada, 'pausaExpiraEm' | 'execExpiraEm' | 'expiraEm'>, now = Date.now()): ExpirationState => {
  if (execution.execExpiraEm != null && execution.execExpiraEm <= now) return 'execucao';
  const pausaExpiraEm = execution.pausaExpiraEm ?? execution.expiraEm;
  return pausaExpiraEm != null && pausaExpiraEm <= now ? 'pausa' : null;
};

const getErrorMessage = (error: unknown) => {
  const responseData = (error as { response?: { data?: { data?: { message?: string }; message?: string } } })?.response?.data;
  return responseData?.data?.message ?? responseData?.message ?? (error instanceof Error ? error.message : 'Não foi possível retomar o formulário.');
};

export default function FillingComp() {
  const [formId, setFormId] = useState<string>('');
  const [open, setOpen] = useState(false);
  const { t, i18n } = useTranslation('translation');
  const dateLocale = i18n.language.startsWith('pt') ? 'pt-BR' : i18n.language.startsWith('es') ? 'es-ES' : 'en-US';
  const [pausedExecutions, setPausedExecutions] = useState<ExecucaoPausada[]>([]);
  const [pausedCursor, setPausedCursor] = useState<PausadasCursor>();
  const [pausedRefreshKey, setPausedRefreshKey] = useState(0);
  const [resumingExecution, setResumingExecution] = useState<ExecutionContext | null>(null);
  const [resumingInicio, setResumingInicio] = useState<number>();
  const recoveryAttempted = useRef(false);

  const { data: authToken } = useQuery({
    queryKey: ['GET_EXECUTIONS_TOKEN'],
    queryFn: AttToken,
    retry: 0,
    refetchOnWindowFocus: false,
  });

  const pausedQuery = useQuery({
    queryKey: ['GET_PAUSED_EXECUTIONS', authToken?.username, authToken?.userEmpresa, pausedCursor?.id, pausedRefreshKey],
    queryFn: async () => {
      if (!authToken) return;
      return execucoesService.listaPausadas(authToken, pausedCursor);
    },
    enabled: Boolean(authToken),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
  });

  useEffect(() => {
    const response = pausedQuery.data;
    if (!response) return;
    setPausedExecutions((current) => pausedCursor ? [...current, ...response.data] : response.data);
  }, [pausedCursor, pausedQuery.data]);


  const { data, isLoading, isFetching, isSuccess, isError } = useQuery({
    queryKey: ['GET_MODELOS'],
    queryFn: async () => {
      try {
        const token = await AttToken();
        if (!token) throw new Error(t('Reports.reportsTable.tokenNotFound'));
        const response = await toast.promise(modelosService.getPreenchimentoWebV2(token), {
          pending: t('Filling.formFilling.buscaModelos'),
          success: t('Filling.formFilling.modelosEncontrados'),
          error: t('Filling.formFilling.erroBuscaModelos')
        }, { toastId: 'modelos-fetch' });
        if (!response) throw new Error(t('Filling.formFilling.erroBuscaModelos'));
        return response;
      } catch (error) {
        throw new Error(t('Filling.formFilling.erroBuscaModelos'));
      }
    },
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
  });

  useEffect(() => {
    if (isSuccess && data?.length) {
      toast.success(t('Filling.formFilling.modelosEncontrados'), { toastId: 'sucesso' });
      trackEventMatomo('preenchimento', 'sucesso', 'carregamento', 'modelos de formulário carregados');
    } if (isError) {
      toast.error(t('Filling.formFilling.erroBuscaModelos'), { toastId: 'erro' });
      trackEventMatomo('preenchimento', 'erro', 'carregamento', 'falha ao carregar modelos de formulário');
    }
  }, [isError, isSuccess, data, t]);

  const loading = isLoading || isFetching;

  const resetPausedExecutions = () => {
    queryClient.removeQueries({ queryKey: ['GET_PAUSED_EXECUTIONS'] });
    setPausedExecutions([]);
    setPausedCursor(undefined);
    setPausedRefreshKey((current) => current + 1);
  };

  const clearLeaseSession = () => {
    sessionStorage.removeItem(EXECUTION_LEASE_SESSION_KEY);
  };

  const buildResumingContext = (checklist: FormToFilling, inicio: number, username: string): ExecutionContext => ({
    inicio,
    checklist: {
      ...checklist,
      pausa: false,
      ultimaDataPausa: null,
      pausaExpiraEm: null,
      expiraEm: null,
      usuario: username,
    } as FormToFilling,
    isResuming: true,
    execExpiraEm: checklist.execExpiraEm,
  });

  useEffect(() => {
    if (!authToken || recoveryAttempted.current) return;
    recoveryAttempted.current = true;

    const serializedSession = sessionStorage.getItem(EXECUTION_LEASE_SESSION_KEY);
    if (!serializedSession) return;

    let session: ExecutionLeaseSession;
    try {
      session = JSON.parse(serializedSession) as ExecutionLeaseSession;
      if (!session.inicio || !session.formId || !session.usuarioOrigem) throw new Error(t('Filling.pausedExecutions.invalidSession'));
    } catch {
      clearLeaseSession();
      return;
    }

    void (async () => {
      try {
        const checklist = await execucoesService.getRawById(authToken, session.usuarioOrigem, session.inicio);
        if (!hasChecklistItems(checklist)) {
          throw new Error('A execução pausada não possui itens para preencher.');
        }
        const control = await execucoesService.iniciarExecucao(authToken, session.inicio);
        if (!control.success) throw new Error(control.data.message);

        setFormId(checklist.id || session.formId);
        setResumingExecution(buildResumingContext(checklist, session.inicio, authToken.username));
        resetPausedExecutions();
        setOpen(true);
      } catch (error) {
        clearLeaseSession();
        void queryClient.invalidateQueries({ queryKey: ['GET_PAUSED_EXECUTIONS'] });
        const message = error instanceof Error ? error.message : t('Filling.pausedExecutions.recoveryError');
        toast.error(t('Filling.pausedExecutions.recoveryHelp', { message }), { toastId: 'execution-lease-recovery' });
      }
    })();
    // A recuperação deve ocorrer no máximo uma vez após carregar a página.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  const resumeExecution = async (execution: ExecucaoPausada) => {
    if (!authToken || resumingInicio) return;

    const user = execution.usuario || execution.username;
    if (!user) {
      toast.error(t('Filling.pausedExecutions.userNotFound'));
      return;
    }

    setResumingInicio(execution.inicio);
    trackEventMatomo('preenchimento', 'click', 'botão', 'retoma formulário pausado');
    try {
      const checklist = await execucoesService.getRawById(authToken, user, execution.inicio);
      if (!hasChecklistItems(checklist)) {
        throw new Error('A execução pausada não possui itens para preencher.');
      }
      const control = await execucoesService.iniciarExecucao(authToken, execution.inicio);
      if (!control.success) {
        toast.error(control.data.message, { toastId: control.data.message });
        resetPausedExecutions();
        return;
      }
      setFormId(checklist.id || execution.formulario);
      sessionStorage.setItem(EXECUTION_LEASE_SESSION_KEY, JSON.stringify({
        inicio: execution.inicio,
        formId: checklist.id || execution.formulario,
        usuarioOrigem: user,
        savedAt: Date.now(),
      } satisfies ExecutionLeaseSession));
      setResumingExecution(buildResumingContext(checklist, execution.inicio, authToken.username));
      resetPausedExecutions();
      setOpen(true);
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message, { toastId: message });
      resetPausedExecutions();
    } finally {
      setResumingInicio(undefined);
    }
  };

  const hasExpiredPendingExecution = () => pausedExecutions.some((execution) => getExpirationState(execution) !== null);

  const columns = [
    {
      name: "nome",
      label: t('name'),
      options: {
        customBodyRender: (value: string) => {
          return (<Typography fontSize='small' sx={{ wordBreak: 'break-word' }}>{value}</Typography>);
        },
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "dataCriacao",
      label: t('Managements.createdAt'),
      options: {
        customBodyRender: (value: number) => {
          const date = new Date(value).toLocaleString(dateLocale);
          return <span style={{ marginRight: '1.5em' }}>{date}</span>;
        },
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "id",
      label: t('Filling.pausedExecutions.id'),
      options: {
        display: false,
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
  ];

  const translatedTextLabels: MUIDataTableOptions = {
    onRowClick: (rowData) => {
      if (pausedQuery.isLoading || pausedQuery.isFetching) {
        toast.info('Aguarde a verificação dos formulários pausados antes de iniciar um novo preenchimento.', { toastId: 'paused-executions-checking' });
        return;
      }
      if (hasExpiredPendingExecution()) {
        toast.warning('Há um formulário pausado ou execução expirada pendente. Retome-o e finalize ou cancele antes de iniciar outro.', { toastId: 'expired-paused-execution-pending' });
        return;
      }
      setFormId(rowData[2]);
      trackEventMatomo('FillingComp', 'click', 'row', 'abre modal');
      setOpen(true);
    },
    textLabels: {
      body: {
        noMatch: `${t('table.noMatch')}`,
        toolTip: `${t('table.toolTip')}`,
        columnHeaderTooltip: column =>
          `${t('table.columnHeaderTooltip')} ${column.label}`
      },
      pagination: {
        next: `${t('table.next')}`,
        previous: `${t('table.previous')}`,
        rowsPerPage: `${t('table.rowsPerPage')}`,
        displayRows: `${t('table.displayRows')}`
      },
      toolbar: {
        search: `${t('table.search')}`,
        filterTable: `${t('table.filterTable')}`
      },
      filter: {
        title: `${t('table.title')}`,
        reset: `${t('table.reset')}`
      },
      viewColumns: {
        title: `${t('table.viewColumnsTitle')}`
      }
    }
  };

  return (
    <>
      {open &&
        <FormFillingModal
          open={open}
          setOpen={(value: boolean) => {
            setOpen(value);
            if (!value) {
              trackEventMatomo('preenchimento', 'click', 'modal', 'fecha seleção de formulário');
              setResumingExecution(null);
            }
          }}
          formId={formId}
          executionContext={resumingExecution}
          onPausedExecutionsChanged={resetPausedExecutions}
          onLeaseLost={() => {
            clearLeaseSession();
            resetPausedExecutions();
            setOpen(false);
            setResumingExecution(null);
            toast.error(t('Filling.pausedExecutions.leaseLost'), { toastId: 'execution-lease-lost' });
          }}
        />
      }
      <Accordion defaultExpanded sx={{ mb: 2, '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: { xs: 2, sm: 3 } }}>
          <Stack direction='row' alignItems='center' justifyContent='space-between' width='100%'>
            <Box>
              <Typography variant='h6' fontWeight={700}>{t('Filling.pausedExecutions.title')}</Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
                {t('Filling.pausedExecutions.description')}
              </Typography>
            </Box>
            <IconButton
              aria-label={t('Filling.pausedExecutions.refresh')}
              onClick={(event) => {
                event.stopPropagation();
                trackEventMatomo('preenchimento', 'click', 'botão', 'atualiza formulários pausados');
                resetPausedExecutions();
              }}
              disabled={pausedQuery.isFetching}
            >
              <RefreshIcon />
            </IconButton>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 }, pt: 0, maxHeight: 230, overflowY: 'auto' }}>
          {pausedQuery.isLoading && <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5, mt: 1.5 }}>
            {[0, 1, 2].map((index) => <Card key={index} variant='outlined' sx={{ p: 1.5, borderRadius: 2 }}>
              <Stack spacing={0.5}>
                <Skeleton variant='text' width='55%' height={24} />
                <Skeleton variant='text' width='38%' height={18} />
                <Skeleton variant='text' width='72%' height={16} />
                <Skeleton variant='text' width='62%' height={16} />
                <Skeleton variant='rounded' width={76} height={28} sx={{ mt: 0.5, ml: 'auto' }} />
              </Stack>
            </Card>)}
          </Box>}
          {pausedQuery.isError && <Typography color='error' sx={{ mt: 1 }}>{t('Filling.pausedExecutions.loadError')}</Typography>}
          {!pausedQuery.isLoading && !pausedQuery.isError && pausedExecutions.length === 0 &&
            <Typography color='text.secondary' sx={{ mt: 1 }}>{t('Filling.pausedExecutions.empty')}</Typography>}
          {pausedExecutions.length > 0 && <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5, mt: 1.5 }}>
            {pausedExecutions.map((execution) => {
              const expirationState = getExpirationState(execution);
              const pausaExpiraEm = execution.pausaExpiraEm ?? execution.expiraEm;
              return <Card
                key={execution.inicio}
                component='article'
                variant='outlined'
                sx={{
                  borderRadius: 2,
                  borderColor: expirationState === 'execucao' ? 'error.main' : expirationState === 'pausa' ? 'warning.main' : undefined,
                  backgroundImage: expirationState === 'execucao'
                    ? 'linear-gradient(135deg, rgba(255, 205, 210, 0.72), rgba(255, 235, 238, 0.9))'
                    : expirationState === 'pausa'
                      ? 'linear-gradient(135deg, rgba(255, 224, 178, 0.74), rgba(255, 248, 225, 0.92))'
                      : 'linear-gradient(135deg, rgba(241, 245, 249, 0.9), rgba(255, 255, 255, 0.96))',
                  transition: 'box-shadow 150ms ease, border-color 150ms ease',
                  '&:hover': { borderColor: 'primary.main', boxShadow: 2 },
                }}
              >
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant='body2' fontWeight={700} sx={{ mb: 1, wordBreak: 'break-word', lineHeight: 1.25 }}>
                  {execution.formularioNome || execution.formulario || t('Filling.pausedExecutions.unnamedForm')}
                </Typography>
                {expirationState && <Chip
                  label={expirationState === 'execucao' ? 'Execução expirada' : 'Pausa expirada'}
                  color={expirationState === 'execucao' ? 'error' : 'warning'}
                  size='small'
                  sx={{ mb: 0.75, fontWeight: 700 }}
                />}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) minmax(110px, 0.8fr)' }, gap: 1 }}>
                  <Box>
                    <Typography variant='caption' color='text.secondary' sx={{ display: 'block', wordBreak: 'break-word', lineHeight: 1.25 }}>
                      <Box component='span' fontWeight={700}>{t('Filling.pausedExecutions.pausedBy')}</Box>{execution.usuario || execution.username || t('Filling.pausedExecutions.unidentifiedUser')}
                    </Typography>
                    <Stack spacing={0.2} sx={{ mt: 1 }}>
                      <Typography variant='caption' sx={{ lineHeight: 1.25 }}><Box component='span' fontWeight={700}>{t('Filling.pausedExecutions.startedAt')}</Box>{new Date(execution.inicio).toLocaleString(dateLocale)}</Typography>
                      {execution.ultimaDataPausa && <Typography variant='caption' sx={{ lineHeight: 1.25 }}><Box component='span' fontWeight={700}>{t('Filling.pausedExecutions.lastPause')}</Box>{new Date(execution.ultimaDataPausa).toLocaleString(dateLocale)}</Typography>}
                      {pausaExpiraEm != null && <Typography variant='caption' sx={{ lineHeight: 1.25 }}><Box component='span' fontWeight={700}>Pausa expira em: </Box>{new Date(pausaExpiraEm).toLocaleString('pt-BR')}</Typography>}
                      {execution.expiraEm && <Typography variant='caption' sx={{ lineHeight: 1.25 }}><Box component='span' fontWeight={700}>{t('Filling.pausedExecutions.expiresAt')}</Box>{new Date(execution.expiraEm).toLocaleString(dateLocale)}</Typography>}
                    </Stack>
                  </Box>
                  <Stack spacing={0.2}>
                    {[
                      [t('Filling.pausedExecutions.client'), execution.nomeCliente || execution.cliente],
                      [t('Filling.pausedExecutions.location'), execution.nomeLocal || execution.local],
                      [t('Filling.pausedExecutions.set'), execution.nomeConjunto || execution.conjunto],
                      [t('Filling.pausedExecutions.unit'), execution.nomeUnidade || execution.unidade],
                    ].filter(([, value]) => Boolean(value)).map(([label, value]) => <Typography key={label} variant='caption' sx={{ wordBreak: 'break-word', lineHeight: 1.25 }}>
                      <Box component='span' color='text.secondary' fontWeight={700}>{label}: </Box>{value}
                    </Typography>)}
                  </Stack>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                  <Button size='small' variant='contained' color='success' onClick={() => resumeExecution(execution)} disabled={Boolean(resumingInicio)}>
                    {resumingInicio === execution.inicio ? t('Filling.pausedExecutions.resuming') : t('Filling.pausedExecutions.resume')}
                  </Button>
                </Box>
              </CardContent>
            </Card>;
            })}
          </Box>}
          {pausedQuery.data?.lastIndex && pausedQuery.data.data.length === PAUSED_EXECUTIONS_PAGE_SIZE && <Stack direction='row' justifyContent='flex-end' sx={{ mt: 1 }}>
            <Button onClick={() => {
              trackEventMatomo('preenchimento', 'click', 'botão', 'carrega mais formulários pausados');
              setPausedCursor(pausedQuery.data?.lastIndex);
            }} disabled={pausedQuery.isFetching}>{t('Filling.pausedExecutions.loadMore')}</Button>
          </Stack>}
        </AccordionDetails>
      </Accordion>
      {loading ? <Skeleton variant='rounded' height={400} width={'100%'} sx={{ marginTop: '10px' }} /> :
        <ThemeProvider theme={getMuiTheme()}>
          <MUIDataTable
            title={t('Filling.formExecution')}
            data={data?.map((item: { nome: string, dataCriacao: number, id: string }) => ({
              ...item,
            })) ?? []}
            columns={columns}
            options={{ ...tableOptions, ...translatedTextLabels }}
          />
        </ThemeProvider>}
    </>
  );
}
